// src/components/Dashboard.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { parse } from "date-fns";
import { useAuth } from "../useAuth";
import { PrintStyles } from "./printStyles";

import { PrintLayout } from "./print";


import FilterBar from "./FilterBar";
import StatisticsBar from "./StatisticsBar";
import Charts from "./Charts";

// ---------------------- Styles ----------------------
const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: ${({ $light }) => ($light ? "#FFFFFF" : "#191B27")};
  color: ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
  
  @media print {
    background-color: #FFFFFF !important;
    color: #191B27 !important;
  }
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 30px;
  background-color: ${({ $light }) => ($light ? "#FFFFFF" : "#191B27")};
  border-bottom: 2px solid #2D354A;
  flex-wrap: wrap;
  gap: 15px;
`;

const Logo = styled.img`
  width: 200px;
  height: auto;
`;

const Title = styled.h1`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  color: ${({ $light }) => ($light ? "#2D354A" : "#DCE4F2")};
  font-size: 28px;
  margin: 0;
  @media (max-width: 900px) {
    position: static;
    transform: none;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Montserrat", sans-serif;
  font-size: 15px;
  color: ${({ $light }) => ($light ? "#2D354A" : "#DCE4F2")};
`;

const LiveDot = styled.div`
  width: 8px;
  height: 8px;
  background-color: #00d160;
  border-radius: 50%;
  animation: pulse 2s infinite;
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const Button = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const LogoutButton = styled(Button)`
  border: 3px solid ${({ $light }) => ($light ? "#191B27" : "#0093FF")};
  background-color: ${({ $light }) => ($light ? "#FFFFFF" : "#2D354A")};
  color: ${({ $light }) => ($light ? "#191B27" : "#FFFFFF")};
  &:hover {
    background-color: ${({ $light }) => ($light ? "#191B27" : "#0093FF")};
    color: ${({ $light }) => ($light ? "#DCE4F2" : "white")};
  }
`;

const ContentArea = styled.div`
  padding: 0 30px 30px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-family: "Montserrat", sans-serif;
  font-size: 50px;
`;

// ---------------------- Component ----------------------
function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function Dashboard() {

const PRINT_PRIORITY = [
  "metricTonsOverTime",
  "categories",
];

  
  const priorityCharts = ["metricTonsOverTime", "categories"];
  const { currentUser, logout } = useAuth();
  const [isLightMode, setIsLightMode] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dashboardRef = useRef();




const [monthRange, setMonthRange] = useState(12);

const twelveMonthsAgo = new Date();
twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
const defaultStartDate = twelveMonthsAgo.toISOString().split("T")[0];

const [filters, setFilters] = useState({
  startDate: defaultStartDate,
  endDate: "",
  category: [],
  buyer: [],
  seller: [],
  country: [],
  charts: ["metricTonsOverTime", "categories"],
});


useEffect(() => {
  const loadData = async () => {
    try {
      setLoading(true);
      const response = await fetch(process.env.PUBLIC_URL + "/PRY_Dash.json");
      if (!response.ok) throw new Error("Failed to load data file");
      const rows = await response.json();
      setRawData(rows);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  loadData();
}, []);

  // ✅ Normalize data
const processedData = useMemo(() => rawData, [rawData]);

const debouncedFilters = useDebounce(filters, 300);

const filteredData = useMemo(() => {
  const start = debouncedFilters.startDate ? new Date(debouncedFilters.startDate).getTime() : null;
  const end = debouncedFilters.endDate ? new Date(debouncedFilters.endDate).getTime() : null;
  return processedData.filter((row) => {
    if (start && new Date(row.Date).getTime() < start) return false;
    if (end && new Date(row.Date).getTime() > end) return false;
    if (debouncedFilters.category.length > 0 && !debouncedFilters.category.includes(row.Category)) return false;
    if (debouncedFilters.buyer.length > 0 && !debouncedFilters.buyer.includes(row.buyer)) return false;
    if (debouncedFilters.seller.length > 0 && !debouncedFilters.seller.includes(row.seller)) return false;
    if (debouncedFilters.country.length > 0 && !debouncedFilters.country.includes(row.country)) return false;
    return true;
  });
}, [processedData, debouncedFilters]);

  // ✅ Filter options
  const filterOptions = useMemo(() => {
    const uniq = (arr) =>
      Array.from(new Set(arr.filter((v) => v && String(v).trim() !== "" && v !== "Unknown")));
    const alpha = (arr) => uniq(arr).sort((a, b) => a.localeCompare(b));
    return {
      categories: alpha(processedData.map((r) => r.Category)),
      buyers: alpha(processedData.map((r) => r.buyer)),
      sellers: alpha(processedData.map((r) => r.seller)),
      countries: alpha(processedData.map((r) => r.country)),
    };
  }, [processedData]);
// Force Recharts SVG to recalc size before print

  // ---------------------- UI ----------------------
  if (loading)
    return (
      <DashboardContainer $light={isLightMode}>
        <LoadingContainer>Loading Maritime Imports Data…</LoadingContainer>
      </DashboardContainer>
    );
  if (error)
    return (
      <DashboardContainer $light={isLightMode}>
        <LoadingContainer>Error: {error}</LoadingContainer>
      </DashboardContainer>
    );

// ------- Print Filter Summary Logic -------


const formatPrintDateRange = (start, end) => {
  const format = (d) =>
    new Date(d).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  // Neither selected
  if (!start && !end) {
    return "January 1, 2024 - May 31, 2026";
  }

  // Start only
  if (start && !end) {
    return `${format(start)} – May 31, 2026`;
  }

  // End only (start date is fixed)
  if (!start && end) {
    return `January 1, 2024 – ${format(end)}`;
  }

  // Both selected
  return `${format(start)} – ${format(end)}`;
};


// 🔥 FIX: Guarantee filters are always arrays
const normalizeArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map(v => v.trim())
      .filter(v => v);
  }
  return [];
};

const summarize = (selected, all) => {
  const sel = normalizeArray(selected);
  const allArr = normalizeArray(all);

  // Empty OR full selection both mean "All"
  if (sel.length === 0 || sel.length === allArr.length) {
    return "All";
  }

  if (sel.length <= 3) {
    return sel.join(", ");
  }

  return `${sel.slice(0, 3).join(", ")} + ${sel.length - 3} more`;
};

const printSummary = {
  dateRange: formatPrintDateRange(filters.startDate, filters.endDate),
  category: summarize(normalizeArray(filters.category), filterOptions.categories),
  buyer: summarize(normalizeArray(filters.buyer), filterOptions.buyers),
  seller: summarize(normalizeArray(filters.seller), filterOptions.sellers),
  country: summarize(normalizeArray(filters.country), filterOptions.countries),
};



// ---------- PRINT STAT MATH (MATCHES STATSBAR EXACTLY) ----------

// Always force numbers
const totalMetricTons = filteredData.reduce(
  (sum, row) => sum + Number(row.metricTons || 0),
  0
);

const totalValue = filteredData.reduce(
  (sum, row) => sum + Number(row.totalValue || 0),
  0
);

// Convert tons → kg
const totalKg = totalMetricTons * 1000;

// Weighted average, same as StatsBar
const avgValPerKg = totalKg > 0 ? totalValue / totalKg : 0;

// Formatters
const fmt2 = (num) =>
  Number(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const fmt0 = (num) =>
  Number(num).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

// Final stats object for print
const printStats = {
  metricTons: fmt2(totalMetricTons),
  totalValue: fmt0(totalValue),
  valPerKg: fmt2(avgValPerKg)
};


return (
    <DashboardContainer $light={isLightMode}>
      <PrintStyles />
      

<div id="dashboard-wrapper" className="print-only">
<PrintLayout
  summary={printSummary}
  stats={printStats}
  isLightMode={isLightMode}
charts={(() => {
  const titleMap = {
    metricTonsOverTime: "Metric Tons Over Time",
    timeSeries: "Imports Over Time",
    categories: "Category Distribution",
    buyers: "Top Domestic Importers",
    sellers: "Top International Suppliers",
    countries: "Country Distribution",
    ports: "Ports of Arrival",
    hsCodes: "Top HS Codes"
  };

  // 1️⃣ Force priority charts to the front (print only)
  const orderedCharts = [
    ...PRINT_PRIORITY.filter(c => filters.charts.includes(c)),
    ...filters.charts.filter(c => !PRINT_PRIORITY.includes(c)),
  ];

  // 2️⃣ Cap total charts for print
  return orderedCharts.slice(0, 7).map(chartType => ({
    title: titleMap[chartType] || chartType,
    chartType,
    data: filteredData,
  }));
})()}

/>


</div>



{/* --- NORMAL ON-SCREEN DASHBOARD --- */}
<div ref={dashboardRef} className="no-print">

  {/* Header */}
  <Header $light={isLightMode}>
    <Logo src={isLightMode ? "/PRY_Logo_Light.png" : "/PRY_Logo.png"} />
    <Title $light={isLightMode}>Peer Imports Dashboard</Title>

    <HeaderRight>
      <Button
        onClick={() => setIsLightMode(!isLightMode)}
        style={{
          border: "3px solid #0093FF",
          backgroundColor: isLightMode ? "#FFFFFF" : "#2D354A",
          color: isLightMode ? "#191B27" : "#DCE4F2",
        }}
      >
        {isLightMode ? "Light Mode" : "Dark Mode"}
      </Button>

      <StatusIndicator $light={isLightMode}>
        <LiveDot /> Live
      </StatusIndicator>

      <StatusIndicator $light={isLightMode}>
        Updated: {new Date().toLocaleDateString()}
      </StatusIndicator>

      <LogoutButton $light={isLightMode} onClick={logout}>
        Logout
      </LogoutButton>
    </HeaderRight>
  </Header>

  
{/* FILTERS + STATISTICS (normal screen only) */}
<ContentArea className="no-print">
  <FilterBar
    filters={filters}
    setFilters={setFilters}
    filterOptions={filterOptions}
    filteredData={filteredData}
    processedData={processedData}
    isLightMode={isLightMode}
  />

  <StatisticsBar
    className="statistics-bar"
    data={filteredData}
    isLightMode={isLightMode}
  />
</ContentArea>

  {/* NORMAL CHART GRID */}
  <ContentArea>
    <div
      id="charts-grid-container"
      className="charts-grid"
      style={{
        marginTop: "20px",
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "30px",
      }}
    >
      {filters.charts && filters.charts.length > 0 ? (
  [...filters.charts]
    .sort((a, b) => {
      const aPri = priorityCharts.includes(a) ? 0 : 1;
      const bPri = priorityCharts.includes(b) ? 0 : 1;
      return aPri - bPri;
    })
    .map((chart) => (
      <div key={chart} data-chart-type={chart} style={{ width: "100%" }}>
       <Charts
  data={filteredData}
  chartType={chart}
  isLightMode={isLightMode}
  monthRange={monthRange}
/>
      </div>
    ))
      ) : (
        <div
          style={{
            gridColumn: "1 / -1",
            textAlign: "center",
            padding: "40px",
            fontFamily: "Montserrat",
            color: isLightMode ? "#191B27" : "#DCE4F2",
            fontSize: "24px",
            fontWeight: "600",
          }}
        >
          Select one or more charts from the dropdown
        </div>
      )}
    </div>
  </ContentArea>
</div> {/* closes no-print wrapper */}


</DashboardContainer>
);
}

export default Dashboard;